import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react"
import { Link, useParams } from "react-router-dom"
import { useSessionAnalytics } from "@/lib/useSessionAnalytics"
import { track } from "@/lib/analytics"
import { withReference } from "@/lib/requestIds"
import { Beam } from "../nira2/ds/Beam"
import { NiraBar } from "../nira2/ds/NiraBar"
import { Rail } from "../nira2/answer/Rail"
import { usePageTitle, useStoredTheme } from "../nira2/answer/hooks"
import { uploadReason } from "../nira2/sim/SimStart"
import { uploadRefusalMessage } from "@/lib/quotaGate"
import "../nira2/answer/answer.css"
import "../nira2/answer/answer-v4.css"
import "../nira2/case/case.css"
import "../nira2/sim/sim.css"
import { filterSimFiles, SIM_ACCEPT } from "../oio-sim/files"
import { SimBanner } from "../oio-sim/SimBanner"
import type { RunThroughThread, SimEngine, Stages } from "./engine"
import { DraftMatter } from "./DraftMatter"
import { CheckpointActions, CheckpointCard, noteHasMore, type CheckpointCardPayload } from "./cards/CheckpointCard"
import { Markdown } from "../nira2/ds/Markdown"
import { DocumentRequestCard } from "./cards/DocumentRequestCard"
import { usePasteFiles } from "./usePasteFiles"
import { EditLimitLine, noteEditRefusal, refreshEditAllowance } from "./editLimit"
import { RunList, type RunStep } from "./RunList"
import { SectionTabs, type SectionTab } from "./SectionTabs"
import { useStepClock } from "./useStepClock"
import { SourcesList } from "./SourcesList"
import type { SourceRef } from "./sources"
import { trackAskAnswered, trackAskShown, trackRefused, trackUploadFailed, trackUploaded, trackStoppedEarly, uploadFailReason, type AskAnswer } from "./simAnalytics"
import { unavailableMessage, uploadedMessage } from "./cards/messages"
import { QuestionCard, type QuestionPayload } from "./cards/QuestionCard"
import { allChosen, changeOf, currentStage, draftChecked, draftProgress, originalOf, rupees } from "./model"
import type { LandedStage, Row, RowChangedPayload, StopPayload } from "./runThrough"
import type { SimAskKind } from "@/lib/analytics"
import "./decisions.css"

/** Asks already counted as shown, per tab. */
const shownAsks = new Set<string>()

const newId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`)

/**
 * The decisions page (handoff 4, Sim-Decisions), for either order: one run, landing section by
 * section, with the draft on top once it exists and a Change on every row. The agent's asks (a stop,
 * a question, a document request, a stage checkpoint) are cards at the top; there is no chat and no
 * step counts: the record of the case is its rows. Everything is rebuilt from the server
 * on load, so a tab closed mid-run reopens where the run is.
 */
export default function Decisions({ engine }: { engine: SimEngine }) {
  const { threadId = "" } = useParams<{ threadId: string }>()
  useSessionAnalytics(engine.app, threadId)
  useStoredTheme()
  const [attempt, setAttempt] = useState(0)
  const [loadError, setLoadError] = useState("")
  const [histOpen, setHistOpen] = useState(false)
  /** Stages whose checkpoint the person confirmed in this tab (later reloads infer it from the next stage landing). */
  const [signed, setSigned] = useState<string[]>([])
  /** The tab the person picked (or a jump chose), and the item a "View" link highlights. */
  const [sel, setSel] = useState("")
  const [focus, setFocus] = useState<{ stage: string; rowId?: string } | null>(null)
  /** Tabs opened in this tab: a landed section not yet opened reads as "new". */
  const [seen, setSeen] = useState<string[]>([])
  const view = engine.useThread(threadId)
  const title = view?.title || engine.untitled
  const { download, exporting, exportError } = engine.useDraftExport(threadId, title)
  const { SECTIONS, draftPath } = useMemo(() => ({
    SECTIONS: engine.sections,
    draftPath: (id: string) => `${engine.base}/${encodeURIComponent(id)}/draft`,
  }), [engine])

  useEffect(() => {
    const ac = new AbortController()
    setLoadError("")
    engine.hydrate(threadId, ac.signal).catch((err: unknown) => {
      if (!ac.signal.aborted) setLoadError(withReference(err instanceof Error && err.message ? err.message : "Something went wrong.", err))
    })
    engine.hydrateDocs(threadId, ac.signal).catch(() => undefined)
    return () => ac.abort()
  }, [engine, threadId, attempt])

  const stages: Stages = view?.stages ?? {}
  const running = view?.status === "running"
  const d = draftChecked(stages)
  const progress = draftProgress(view?.draft ?? null, view?.status ?? "idle")
  const hasDraft = progress.ready
  const landedAll = SECTIONS.every((s) => stages[s.stage])
  const done = landedAll && !running && hasDraft
  const crumbName = shortTitle(plainTitle(title, engine.untitled))
  const statusWord = view?.rerun ? "Updating" : hasDraft ? "Draft ready" : "Drafting"
  usePageTitle(`${title} · ${engine.short} draft`)
  const send = async (message: string) => {
    try {
      await engine.api.postTurn(threadId, message, newId())
      return true
    } catch {
      return false
    }
  }
  // Messages typed while a run is going wait here (and in the service) until the next turn reads them.
  const [queued, setQueued] = useState<{ id: string; message: string }[]>([])
  const refreshQueue = () => {
    void engine.api.getQueue?.(threadId).then((r) => setQueued(r.queued.map((q) => ({ id: q.request_id, message: q.message })))).catch(() => undefined)
  }
  useEffect(() => { setQueued([]); refreshQueue() }, [engine, threadId, running]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!queued.length) return
    const t = setInterval(refreshQueue, 4000)
    return () => clearInterval(t)
  }, [queued.length, threadId]) // eslint-disable-line react-hooks/exhaustive-deps
  const sendFree = async (message: string) => {
    const id = newId()
    try {
      const res = (await engine.api.postTurn(threadId, message, id)) as { queued?: boolean } | undefined
      if (res?.queued) setQueued((q) => [...q, { id, message }])
      return true
    } catch {
      return false
    }
  }
  const unqueue = async (id: string) => {
    try { await engine.api.removeQueued?.(threadId, id) } catch { /* delivered meanwhile */ }
    refreshQueue()
  }
  // A run that ended without finishing, with nothing pending to answer: the officer needs a way on.
  const interrupted = !!view && !running && !view.pendingQuestion && !view.stop && (view.status === "failed" || view.status === "error")
  const now = currentStage(SECTIONS, engine.draftingNow, stages)
  const waiting = !!view?.pendingQuestion && !running
  // A checkpoint for a stage whose rows are already on the page is answered in that section, not in a second card.
  const pendingCp = view?.pendingQuestion?.kind === "checkpoint" ? (view.pendingQuestion as unknown as CheckpointCardPayload) : null
  const review = pendingCp && pendingCp.stage !== "continue" && stages[pendingCp.stage] && !running ? pendingCp : null

  // Each ask the agent waits on is counted once per tab, not again on a reload or a re-render.
  const pending = view?.pendingQuestion
  const ask: { kind: SimAskKind; stage: string } | null = view?.stop
    ? { kind: "stop", stage: now.stage }
    : pending && !running && (pending.kind === "question" || pending.kind === "document" || pending.kind === "checkpoint")
      ? { kind: pending.kind, stage: String((pending.kind === "checkpoint" ? pending.stage : pending.kind === "document" ? pending.needed_for_stage : "") || now.stage) }
      : null
  const askKey = ask ? `${threadId}:${JSON.stringify(view?.stop ?? pending)}` : ""
  useEffect(() => {
    if (!ask || shownAsks.has(askKey)) return
    shownAsks.add(askKey)
    trackAskShown(engine.kind, ask.kind, ask.stage)
  }, [ask, askKey, engine.kind])
  const answered = (kind: SimAskKind, how: AskAnswer) => {
    trackAskAnswered(engine.kind, kind, how)
    if (how === "stop_here") trackStoppedEarly(engine.kind, progress.written, progress.total)
  }


  // What the pending question is about, if it names a section (and an item in it) that has rows on the page.
  const ref = view && !running && !view.stop ? refOf(view.pendingQuestion, stages, SECTIONS) : null
  const attention = !!view?.pendingQuestion && !running && !view.stop && view.pendingQuestion.kind !== "stop"
  // Tabs come from the sections that have rows; a section appears once it has content.
  const landedSections = SECTIONS.filter((s) => stages[s.stage])
  const activeTab = landedSections.some((s) => s.stage === sel) ? sel : landedSections[0]?.stage ?? ""
  const confirmedOf = (stage: string) => {
    const i = SECTIONS.findIndex((x) => x.stage === stage)
    return !review && (signed.includes(stage) || (view?.mode === "guided" && stage !== "drafting" && SECTIONS.slice(i + 1).some((x) => stages[x.stage])))
  }
  const toneOf = (stage: string): SectionTab["tone"] =>
    ref?.stage === stage ? "ask" : stages[stage]?.stale ? "idle" : confirmedOf(stage) ? "ok" : !seen.includes(stage) && stage !== activeTab ? "new" : "idle"
  const tabs: SectionTab[] = landedSections.map((s) => ({ id: s.stage, label: s.title, tone: toneOf(s.stage) }))
  // A new question moves the page to the section it is about; otherwise the person's tab stays.
  useEffect(() => { if (ref) setSel(ref.stage) }, [askKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab) setSeen((x) => (x.includes(activeTab) ? x : [...x, activeTab])) }, [activeTab])
  // A link into a row of a section on this page (the draft's margin notes) opens that tab.
  useEffect(() => {
    const h = window.location.hash
    const hit = SECTIONS.find((s) => h === `#nsd-${s.stage}` || h.startsWith(`#nsd-row-${s.stage}-`))
    if (!hit) return
    setSel(hit.stage)
    if (h.startsWith("#nsd-row-")) setFocus({ stage: hit.stage, rowId: h.slice(`#nsd-row-${hit.stage}-`.length) })
  }, [SECTIONS, threadId])
  const jump = (to: { stage: string; rowId?: string }) => { setSel(to.stage); setFocus(to) }

  // The Run list: every step with its live time. Steps are the sections, then the draft.
  const runSteps = [...SECTIONS.map((s) => ({ stage: s.stage, n: s.n, title: s.title })), { stage: "drafting", n: "", title: "Draft" }]
  const isActive = (stage: string) => running && (stage === "drafting" ? now.stage === "drafting" && !d.checked : !!stages[stage]?.stale || (!stages[stage] && now.stage === stage))
  const elapsedOf = useStepClock(runSteps.map((s) => ({ id: s.stage, active: isActive(s.stage), durationMs: stages[s.stage]?.duration_ms })))
  const runList: RunStep[] = runSteps.map((s) => {
    const landed = stages[s.stage]
    let status = "Not started"
    let tone: RunStep["tone"] = "idle"
    if (isActive(s.stage)) { status = "Running"; tone = "run" }
    else if (ref?.stage === s.stage) { status = "1 question"; tone = "ask" }
    else if (s.stage === "drafting") {
      if (d.written) { status = d.checked ? "Written and checked" : "Written"; tone = d.checked ? "ok" : "new" }
    } else if (landed) {
      if (confirmedOf(s.stage)) { status = "Confirmed"; tone = "ok" }
      else if (!seen.includes(s.stage)) { status = `${landed.rows.length} new`; tone = "new" }
      else { status = "Landed" }
    }
    return { id: s.stage, name: s.title, section: s.n ? `Section ${s.n}` : "Draft", elapsed: elapsedOf(s.stage), status, tone, tab: s.stage !== "drafting" && !!landed }
  })

  return (
    <div className="na2 na4 ncf nsd">
      <SimBanner />
      <NiraBar
        onMenu={() => setHistOpen((v) => !v)} menuOpen={histOpen} menuControls="nira-hlist"
        crumbs={[{ label: "Order simulator", to: "/simulator" }, { label: `Order draft · ${engine.short}` }, { label: crumbName }]}
        actions={(
          <>
            <span className="vchip" role="status">{statusWord}</span>
            {hasDraft ? (<>
            <Link className="nr-bar-act" data-variant="primary" to={draftPath(threadId)}><span>Open the draft</span></Link>
            <button type="button" className="nr-bar-act" disabled={exporting} onClick={() => void download()}><span>{exporting ? "Building…" : "Export .docx"}</span></button>
            </>) : null}
          </>
        )}
      />
      {/* The same history as everywhere in Nira; this case sits in its "Order simulator" group. */}
      <Rail threadId={threadId} histOpen={histOpen} onHist={setHistOpen} refreshKey={running ? 1 : 0} homeTo="/simulator" logoTo="/ask" drawerOnly scope={engine.kind} />
      <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
      <main className="nsd-frame">
        <div className="nsd-col">
          {loadError ? (
            <div className="ncf-msg"><h1>The case could not be opened.</h1><p>{loadError} <button type="button" className="pro-link" onClick={() => setAttempt((n) => n + 1)}>Try again</button></p></div>
          ) : !view ? (
            <p className="ncf-msg" role="status">Opening the case…</p>
          ) : (
            <>
              {view.stop ? <StopCard key={JSON.stringify(view.stop)} engine={engine} threadId={threadId} stop={view.stop} send={send} onAnswered={(how) => answered("stop", how)} /> : null}
              {/* What the agent is waiting on, above the tabs so the answer never needs a scroll. It comes from the snapshot, so it is here after a refresh too. */}
              {attention && view.pendingQuestion ? (
                <section className="nsd-attn" aria-label="Agent needs your attention">
                  <p className="nsd-attn-k">Agent needs your attention</p>
                  {review ? (
                    <div className="nsd-review" key={JSON.stringify(review)}>
                      <Markdown className={noteHasMore(review.summary_markdown) ? "nsd-review-note" : "nsd-review-ask"}>{review.summary_markdown.trim() || "Check the rows. Anything wrong or missing?"}</Markdown>
                      <CheckpointActions focusFix payload={review} send={send}
                        onAnswered={(how, option) => { if (how === "option" && option === review.options[0]) setSigned((x) => [...x, review.stage]); answered("checkpoint", how === "text" ? "text" : option === "Resume" ? "resume" : option === "Stop here" ? "stop_here" : "option") }} />
                    </div>
                  ) : (
                    <Pending engine={engine} threadId={threadId} pending={view.pendingQuestion} send={send} onAnswered={answered} landed={Object.keys(stages)} />
                  )}
                  {ref ? <button type="button" className="nsd-attn-view" onClick={() => jump(ref)}>View {ref.label} →</button> : null}
                </section>
              ) : null}
              {running && !landedAll && !view.rerun ? <Working now={now.now} guided={view.mode === "guided"} /> : null}
              {landedAll ? (
                <Where engine={engine} view={view} threadId={threadId} progress={progress} running={running} draft={d} onExport={() => void download()} exporting={exporting} />
              ) : null}
              {exportError ? <p className="ncf-said" role="alert">{exportError}</p> : null}
              {view.rerun ? (
                <div className="nsd-rerun" role="status">
                  <Beam state="working" label={null} />
                  <div><b>Re-running {view.rerun.stages.filter((s) => s !== "drafting" && s !== view.rerun?.from_stage).map(stageNo).join(", ") || stageNo(view.rerun.from_stage)} and the sections that read it.</b><span>Rows that depend on the change dim; the rest stays readable.</span></div>
                </div>
              ) : null}
              {tabs.length ? (
                <>
                  <SectionTabs tabs={tabs} selected={activeTab} onSelect={(id) => { setSel(id); setFocus(null) }} />
                  <div id="nsd-panel" role="tabpanel" aria-labelledby={`nsd-tab-${activeTab}`}>
                    {SECTIONS.map((s, i) => {
                      const landed = stages[s.stage]
                      return landed && s.stage === activeTab ? (
                        <StageSection key={s.stage} engine={engine} threadId={threadId} n={s.n} title={s.title} stage={s.stage} landed={landed}
                          changes={view.changes} first={i === 0} locked={running} sources={view.sources[s.stage]}
                          needsInput={ref?.stage === s.stage} confirmed={confirmedOf(s.stage)}
                          highlight={focus?.stage === s.stage ? focus.rowId : undefined} />
                      ) : null
                    })}
                  </div>
                </>
              ) : null}
              {queued.length ? (
                <div className="nsd-pause" role="status" aria-label="Queued messages">
                  {queued.map((q) => (
                    <div key={q.id} className="nsd-stop-acts">
                      <span><b>Queued</b> — Nira will read this next: {q.message}</span>
                      <button type="button" className="ncf-btn" onClick={() => void unqueue(q.id)}>Remove</button>
                    </div>
                  ))}
                </div>
              ) : null}
              {interrupted ? (
                <div className="nsd-stop">
                  <b>{view.error?.message ? `The run stopped: ${view.error.message}` : "The run stopped before finishing."}</b>
                  <span>Continue from where it left off?</span>
                  <div className="nsd-stop-acts"><button type="button" className="ncf-btn" onClick={() => void send("continue")}>Continue</button></div>
                </div>
              ) : null}
            </>
          )}
        </div>
        <aside className="nsd-side">
          <RunList steps={runList} selected={activeTab} onOpen={(id) => { setSel(id); setFocus(null) }} />
          {running ? <p className="nsd-note">Close this tab; Nira finishes and the workspace keeps it.</p> : null}
          {/* The workspace card sits under the run's column, in the right gutter the page leaves empty. */}
          {view?.snapshotLoaded ? <DraftMatter key={threadId} kind={engine.kind} threadId={threadId} caseId={view.caseId} placement="rail" /> : null}
        </aside>
      </main>
      {view ? <Composer disabled={running && !engine.api.getQueue} running={running} send={sendFree} onSent={() => track("sim_turn_submitted", { kind: "ask" })} /> : null}
    </div>
  )

  function stageOf(st: string) {
    return SECTIONS.find((x) => x.stage === st) ?? { n: "", title: st === "drafting" ? "Draft" : st }
  }
  function stageNo(st: string) {
    const s = SECTIONS.find((x) => x.stage === st)
    return s ? `${s.n} ${s.title}` : "the draft"
  }
}

/** "OIO draft: Acme Traders" is the stored title; the bar already says what kind of draft it is. */
const plainTitle = (t: string, untitled: string) => {
  const m = t.match(/^[A-Za-z]{3}\s+draft\s*[:\u2013\u2014-]\s*(.+)$/i)
  return m ? m[1].trim() : t === untitled ? "Untitled" : t
}
const shortTitle = (t: string) => (t.length > 40 ? `${t.slice(0, 38)}…` : t)

/** A stage's header: its number and name, and where it stands (landed, re-running, waiting for you). */
function StageHead({ n, title, status, waiting, id }: { n: string; title: string; status: string; waiting?: boolean; id?: string }) {
  return (
    <div className="nsd-sh">
      <h2 id={id} className="ncf-eyebrow">{n} · {title}</h2>
      <span className={`ncf-doc-u nsd-st${waiting ? " wait" : ""}`}>{status}</span>
    </div>
  )
}

/** While the run goes: what Nira is doing now, in a line, and that nothing waits on the reader. */
function Working({ now, guided }: { now: string; guided: boolean }) {
  return (
    <div className="nsd-working" role="status" aria-live="polite">
      <Beam state="working" size="hero" label={null} />
      <div>
        <h1>{now}</h1>
        <p>{guided ? "Nira will ask when something is missing or unclear." : "Nothing waits on you."} Each part lands below as it is done.</p>
      </div>
    </div>
  )
}

/** Where it stands: the verdict, the glance, and the draft card, above 01. */
function Where({ engine, view, threadId, progress, running, draft, onExport, exporting }: {
  engine: SimEngine; view: RunThroughThread; threadId: string; progress: ReturnType<typeof draftProgress>; running: boolean
  draft: ReturnType<typeof draftChecked>; onExport: () => void; exporting: boolean
}) {
  const hasDraft = progress.ready
  const line = engine.verdict(view.stages)
  const cells = engine.glance(view.stages, view.changes)
  const chosen = allChosen(engine.sections, view.stages)
  const sectionCount = Object.keys(view.draft?.sections ?? {}).length
  // A finished older run can be missing sections: say how many are written rather than call it complete.
  const gaps = hasDraft && progress.total > 0 && !progress.complete
  const written = Object.values(view.draft?.sections ?? {}).filter((x) => x.markdown.trim()).length
  // The sections a change says most about come first.
  const state = view.rerun ? `re-writing ${[...view.rerun.sections].filter((x) => x !== "self-check").sort((a, b) => engine.rankSection(a) - engine.rankSection(b)).map(engine.sectionWord).slice(0, 2).join(" and ")}`
    : running && !draft.checked ? (written ? `writing · ${written} ${written === 1 ? "section" : "sections"} so far` : "being written")
    : draft.checked ? `checked${draft.notes ? ` · ${draft.notes} ${draft.notes === 1 ? "note" : "notes"}` : ""}` : "written"
  return (
    <section className="nsd-where" aria-labelledby="nsd-verdict">
      <p className="ncf-eyebrow">Where it stands</p>
      <h1 id="nsd-verdict">{line ?? "The decisions have landed. The draft follows."}</h1>
      {chosen.length ? <p className="nsd-read">{chosen.length === 1 ? "One call was Nira's own default" : `${chosen.length} calls were Nira's own defaults`}; each is marked and can be changed below.</p> : null}
      <div className="nsd-glance">
        {cells.map((c) => (
          <div key={c.label}>
            <span className="k">{c.label}</span>
            <span className={`v${c.mono ? " mono" : ""}`}>{c.value}{c.chip ? <span className="nsd-chip">{c.chip}</span> : null}</span>
            <span className="s">{c.sub}</span>
          </div>
        ))}
      </div>
      <div className="nsd-draft">
        <div className="nsd-draft-h">
          {hasDraft ? (
            <>
              <b>The draft · {engine.orderName}{gaps ? ` · ${progress.written} of ${progress.total} sections written` : sectionCount ? ` · ${sectionCount} ${sectionCount === 1 ? "section" : "sections"}` : ""} · {state}</b>
              <div>
                <Link className="ncf-btn primary" to={`${engine.base}/${encodeURIComponent(threadId)}/draft`}>Open the draft</Link>
                <button type="button" className="ncf-btn" disabled={exporting} onClick={onExport}>Export .docx</button>
              </div>
            </>
          ) : (
            <b>The draft · {engine.orderName} · {progress.label}</b>
          )}
        </div>
        <p>{hasDraft ? "The reasons behind every paragraph are the rows below; change a row and the draft follows." : "The draft opens when every section is written."}</p>
      </div>
    </section>
  )
}

/** One numbered section: its rows, each with its Change. */
function StageSection({ engine, threadId, n, title, stage, landed, changes, first, locked, sources, needsInput, confirmed, highlight }: {
  engine: SimEngine; threadId: string; n: string; title: string; stage: string; landed: LandedStage; changes: RowChangedPayload[]; first: boolean; locked: boolean
  sources?: SourceRef[]
  /** The agent's question at the top is about this section. */
  needsInput?: boolean
  /** A row a "View" link points at. */
  highlight?: string
  /** Guided mode, answered and moved on: the section is signed off. */
  confirmed?: boolean
}) {
  const [open, setOpen] = useState("")
  const chosen = new Map(landed.chosen.map((c) => [c.row_id, c]))
  useEffect(() => {
    if (highlight) document.getElementById(`nsd-row-${stage}-${highlight}`)?.scrollIntoView?.({ block: "center", behavior: "smooth" })
  }, [highlight, stage])
  return (
    <section id={`nsd-${stage}`} className={`nsd-sec${first ? " first" : ""}${landed.stale ? " dim" : ""}`} aria-labelledby={`nsd-${stage}-h`}>
      <StageHead id={`nsd-${stage}-h`} n={n} title={title} status={landed.stale ? "Updating" : needsInput ? "For your check" : confirmed ? "Confirmed" : "Landed"} waiting={!!needsInput && !landed.stale} />
      <div className="ncf-docs nsd-rows">
        {landed.rows.map((row) => (
          <div key={row.id} id={`nsd-row-${stage}-${row.id}`} className={highlight === row.id ? "nsd-hl" : undefined}>
            <DecisionLine engine={engine} row={row} stage={stage} chosen={chosen.get(row.id)?.text} mine={changeOf(changes, stage, row.id)}
              original={originalOf(changes, stage, row.id)} open={open === row.id} locked={locked}
              onOpen={() => setOpen(open === row.id ? "" : row.id)} threadId={threadId} />
            {open === row.id ? <ChangePanel engine={engine} threadId={threadId} stage={stage} row={row} onDone={() => setOpen("")} /> : null}
          </div>
        ))}
        <SourcesList sources={sources} />
      </div>
    </section>
  )
}

function DecisionLine({ engine, row, stage, chosen, mine, original, open, locked, onOpen, threadId }: {
  engine: SimEngine; row: Row; stage: string; chosen?: string; mine?: RowChangedPayload; original?: string; open: boolean; locked: boolean; onOpen: () => void; threadId: string
}) {
  const [restoring, setRestoring] = useState(false)
  const isCall = stage === engine.callStage && !!row.call
  const label = isCall ? null : row.label
  const canChange = row.actions.includes("change") || row.actions.includes("change_call")
  const restore = async () => {
    if (!original) return
    setRestoring(true)
    try {
      await engine.api.changeRow(threadId, stage, row.id, original, "Restored Nira's call")
      refreshEditAllowance(threadId)
    } catch (err) {
      noteEditRefusal(threadId, err)
    } finally { setRestoring(false) }
  }
  return (
    <div className={`nsd-row${row.id.startsWith("conflict-") ? " conflict" : ""}${label ? "" : " nolabel"}${isCall ? ` call-${engine.callClass[row.call ?? ""] ?? ""}` : ""}`}>
      {label ? <span className="ncf-doc-u nsd-k">{label}</span> : null}
      <div className="w">
        <span className={row.text === "not stated" ? "ncf-ask nsd-unstated" : undefined}>{row.text}</span>
        {row.amount && !row.call && stage === engine.amountStage ? <span className="mono nsd-amt"> {rupees(row.amount)}</span> : null}
        {mine ? <span className="ncf-ask nsd-you">you chose</span> : null}
        {row.sub ? <span className="s">{row.sub}</span> : null}
        {chosen ? <span className="s"><span className="ncf-ask nsd-chosen">Nira chose</span> {chosen}</span> : null}
        {row.table ? <RowTable table={row.table} /> : null}
        {row.working ? <details className="nsd-working-d"><summary>see the working</summary><p>{row.working}</p></details> : null}
      </div>
      <div className="act">
        {mine && original && isCall ? <button type="button" className="nr nr-btn" data-variant="link" data-size="sm" disabled={locked || restoring} onClick={() => void restore()}>Restore Nira's call</button> : null}
        {row.actions.includes("add_document") ? <AddDocument engine={engine} threadId={threadId} label={row.id === "reply" ? "Add the reply" : "Add a document"} /> : null}
        {canChange ? (
          <button type="button" className="nr nr-btn" data-variant="link" data-size="sm" aria-expanded={open} disabled={locked} onClick={onOpen}>
            {row.actions.includes("change_call") ? "Change the call" : "Change"}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/** A row's small table: the first line is the header; amount columns and a Total line are marked like any document table. */
function RowTable({ table }: { table: string[][] }) {
  const [head, ...body] = table
  const numeric = head.map((_, c) => body.length > 0 && body.every((r) => /^[₹$()\-–\d,.\s%]+$/.test(r[c] ?? "") && /\d/.test(r[c] ?? "")))
  return (
    <div className="nsd-tw">
      <table className="nsd-table">
        <thead><tr>{head.map((h, c) => <th key={c} className={numeric[c] ? "num" : undefined}>{h}</th>)}</tr></thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i} className={/^\s*(total|grand total|net)/i.test(r[0] ?? "") ? "total" : undefined}>
              {head.map((_, c) => <td key={c} className={numeric[c] ? "num" : undefined}>{r[c] ?? ""}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Change, inline under the row (Sim-Change, panel A): the call or the value, a why, and what re-runs. */
function ChangePanel({ engine, threadId, stage, row, onDone }: { engine: SimEngine; threadId: string; stage: string; row: Row; onDone: () => void }) {
  const isCall = row.actions.includes("change_call")
  const [call, setCall] = useState(row.call ?? "")
  const [value, setValue] = useState(row.text === "not stated" ? "" : row.text)
  const [why, setWhy] = useState("")
  const [intent, setIntent] = useState<"" | "established" | "not">("")
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState("")
  const to = isCall ? `${call}${intent ? ` · intent ${intent === "established" ? "established, s.74" : "not established, s.73"}` : ""}` : value.trim()
  const unchanged = isCall ? call === row.call && !intent : value.trim() === row.text
  const go = async () => {
    if (!to || unchanged) return
    setBusy(true)
    setSaid("")
    try {
      await engine.api.changeRow(threadId, stage, row.id, to, why.trim())
      track("sim_turn_submitted", { kind: "change", stage })
      refreshEditAllowance(threadId)
      onDone()
    } catch (err) {
      // The 11th edit on this draft is said inline below (EditLimitLine), with the add-on; nothing else is said.
      if (noteEditRefusal(threadId, err)) setSaid("")
      else setSaid((err as { status?: number })?.status === 409 ? "The run is still going. Change it once this part lands." : "Couldn't make the change. Try again.")
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="nsd-pane" role="group" aria-label={`Change ${row.label || row.id}`}>
      {isCall ? (
        <>
          <p className="nsd-pane-was">Nira's call: <b className={`call-${engine.callClass[row.call ?? ""] ?? ""}`}>{engine.calls.find(([k]) => k === row.call)?.[1] ?? row.call}</b></p>
          <div className="nsd-pane-f"><span className="ncf-eyebrow">Your call</span>
            <div className="nsd-pills">{engine.calls.map(([k, w]) => <button key={k} type="button" className={`nsd-pill${call === k ? " on" : ""}`} aria-pressed={call === k} onClick={() => setCall(k)}>{w}</button>)}</div>
          </div>
          {engine.intent ? (
            <div className="nsd-pane-f"><span className="ncf-eyebrow">Intent</span>
              <div className="nsd-pills">
                <button type="button" className={`nsd-pill${intent === "established" ? " on" : ""}`} aria-pressed={intent === "established"} onClick={() => setIntent(intent === "established" ? "" : "established")}>Established · s.74</button>
                <button type="button" className={`nsd-pill${intent === "not" ? " on" : ""}`} aria-pressed={intent === "not"} onClick={() => setIntent(intent === "not" ? "" : "not")}>Not established · s.73</button>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <label className="nsd-pane-f"><span className="ncf-eyebrow">{row.label || "Value"}</span>
          <input className="nsd-in" value={value} onChange={(e) => setValue(e.target.value)} /></label>
      )}
      <label className="nsd-pane-f"><span className="ncf-eyebrow">Why, in a line{isCall ? " (goes into the finding)" : ""}</span>
        <textarea className="nsd-in serif" rows={2} value={why} onChange={(e) => setWhy(e.target.value)} /></label>
      <div className="nsd-pane-foot">
        <p>{engine.rerunLine(stage, row)}</p>
        <div>
          <button type="button" className="ncf-btn" onClick={onDone}>Cancel</button>
          <button type="button" className="ncf-btn primary" disabled={busy || !to || unchanged} onClick={() => void go()}>{busy ? "Starting…" : "Re-run with this"}</button>
        </div>
      </div>
      {said ? <p className="ncf-said" role="status">{said}</p> : null}
      <EditLimitLine threadId={threadId} />
    </div>
  )
}

function AddDocument({ engine, threadId, label }: { engine: SimEngine; threadId: string; label: string }) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const add = async (list: FileList | null) => {
    const { files, message } = filterSimFiles(Array.from(list ?? []))
    trackRefused(engine.kind, message)
    if (!files.length) return
    setBusy(true)
    const went: File[] = []
    try {
      for (const f of files) {
        try { await engine.uploadDoc(threadId, f); went.push(f) } catch (err) { trackUploadFailed(engine.kind, uploadFailReason(err)); throw err }
      }
      trackUploaded(engine.kind, went, "picker")
      await engine.api.postTurn(threadId, `New documents added: ${files.map((f) => f.name).join(", ")}. Read them and re-run what they change.`, newId())
    } catch { /* the document list says what failed */ } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button type="button" className="nr nr-btn" data-variant="link" data-size="sm" disabled={busy} onClick={() => input.current?.click()}>{busy ? "Adding…" : label}</button>
      <input ref={input} type="file" multiple hidden accept={SIM_ACCEPT} onChange={(e) => { void add(e.target.files); e.target.value = "" }} />
    </>
  )
}

/** A stop: the one thing the draft cannot be written without. A card at the top; nothing covers the page. */
function StopCard({ engine, threadId, stop, send, onAnswered }: { engine: SimEngine; threadId: string; stop: StopPayload; send: (m: string) => Promise<boolean>; onAnswered: (how: AskAnswer) => void }) {
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const card = useRef<HTMLDivElement>(null)
  const [text, setText] = useState("")
  const [error, setError] = useState("")
  const answer = async (message: string, how: AskAnswer): Promise<boolean> => {
    setBusy(true)
    setError("")
    let ok = false
    try {
      ok = await send(message)
    } catch {
      ok = false
    } finally {
      setBusy(false)
    }
    if (!ok) setError("Couldn't reach the simulator. Try again.")
    else onAnswered(how)
    return ok
  }
  const upload = async (picked: File[], via: "picker" | "paste") => {
    const { files, message } = filterSimFiles(picked)
    trackRefused(engine.kind, message)
    if (!files.length) return
    setBusy(true)
    setError("")
    let name = ""
    try {
      for (const f of files) {
        name = f.name
        await engine.uploadDoc(threadId, f)
      }
      trackUploaded(engine.kind, files, via)
    } catch (err) {
      trackUploadFailed(engine.kind, uploadFailReason(err))
      setError(uploadRefusalMessage(err) ?? `Couldn't upload ${name}: ${uploadReason(err)}.`)
      setBusy(false)
      return
    }
    setBusy(false)
    await answer(files.map((f) => uploadedMessage(f.name, stop.question)).join("\n"), "upload")
  }
  const choose = (o: string) => void answer(
    o === "It is not available" ? unavailableMessage(stop.question) : o,
    o === "It is not available" ? "not_available" : o === "Stop here" ? "stop_here" : "option",
  )
  usePasteFiles(card, (files) => void upload(files, "paste"), !busy)
  const free = stop.kind === "required_fact" || stop.kind === "officer_call"
  return (
    <div ref={card} className="nsd-stop" role="alert" tabIndex={-1}>
      <b>{stop.question}</b>
      {stop.why ? <span>{stop.why}</span> : null}
      <div className="nsd-stop-acts">
        {stop.options.map((o) => o === "Add the document" ? (
          <Fragment key={o}><button type="button" className="ncf-btn primary" disabled={busy} onClick={() => input.current?.click()}>{o}</button>
          <span className="nsd-def">or paste an image</span></Fragment>
        ) : (
          <button key={o} type="button" className="ncf-btn" disabled={busy} onClick={() => choose(o)}>{o}</button>
        ))}
        {free ? (
          <form className="nsd-stop-f" onSubmit={(e) => { e.preventDefault(); if (text.trim()) void answer(text.trim(), "text").then((ok) => { if (ok) setText("") }) }}>
            <input className="nsd-in" value={text} placeholder={stop.kind === "officer_call" ? "Or say it in your words" : "The answer"} aria-label="Your answer" onChange={(e) => setText(e.target.value)} />
            <button type="submit" className="ncf-btn primary" disabled={busy || !text.trim()}>Go on</button>
          </form>
        ) : null}
        {stop.default ? <span className="nsd-def">Default: {stop.default}</span> : null}
      </div>
      {error ? <p className="ncf-said" role="alert">{error}</p> : null}
      <input ref={input} type="file" multiple hidden accept={SIM_ACCEPT} onChange={(e) => { const picked = Array.from(e.target.files ?? []); e.target.value = ""; void upload(picked, "picker") }} />
    </div>
  )
}

/** The one thing the agent is waiting on, by kind. A stop has its own card above. */
function Pending({ engine, threadId, pending, send, onAnswered, landed }: {
  landed: string[]
  engine: SimEngine; threadId: string; pending: { kind: string; [key: string]: unknown }; send: (m: string) => Promise<boolean>
  onAnswered: (kind: SimAskKind, how: AskAnswer) => void
}) {
  // A new ask is a new card: a card keeps its sent state until it unmounts.
  const key = JSON.stringify(pending)
  if (pending.kind === "question") return <QuestionCard key={key} payload={pending as unknown as QuestionPayload} send={send} onAnswered={() => onAnswered("question", "text")} />
  if (pending.kind === "document") {
    return <DocumentRequestCard key={key} engine={engine} threadId={threadId} payload={pending as unknown as { description: string; why: string; needed_for_stage: string }} send={send} onAnswered={(how) => onAnswered("document", how)} />
  }
  if (pending.kind === "checkpoint") {
    return <CheckpointCard key={key} engine={engine} payload={{ stage: "", summary_markdown: "", options: [], ...pending } as CheckpointCardPayload} send={send} landed={landed.includes(String(pending.stage ?? ""))}
      onAnswered={(how, option) => onAnswered("checkpoint", how === "text" ? "text" : option === "Resume" ? "resume" : option === "Stop here" ? "stop_here" : "option")} />
  }
  return null
}

/** The sticky composer: change a finding, correct a fact, or ask for a rewrite, in words. */
function Composer({ disabled, running, send, onSent }: { disabled: boolean; running: boolean; send: (m: string) => Promise<boolean>; onSent: () => void }) {
  const [text, setText] = useState("")
  const [busy, setBusy] = useState(false)
  const go = async () => {
    const q = text.trim()
    if (!q || busy || disabled) return
    setBusy(true)
    if (await send(q)) { setText(""); onSent() }
    setBusy(false)
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); void go() } }
  const hint = useMemo(() => (disabled ? "The run is going; ask once it lands." : running ? "Add an instruction; Nira reads it next…" : "Change a finding, correct a fact, or ask for a rewrite…"), [disabled, running])
  return (
    <div className="nsd-fu">
      <div>
        <input aria-label="Ask about this draft" placeholder={hint} value={text} disabled={disabled} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} />
        <button type="button" disabled={disabled || busy || !text.trim()} onClick={() => void go()}>Send</button>
      </div>
    </div>
  )
}

/**
 * What a pending ask is about: the section it names (a checkpoint's `stage`, a document request's
 * `needed_for_stage`, or a `stage` on a question) and, when it names one, the row in it (`row_id`).
 * Only a section with rows on the page can be linked to; an ask that names nothing has no link.
 */
export function refOf(pending: { kind: string; [key: string]: unknown } | null | undefined, stages: Stages, sections: { stage: string; title: string }[]): { stage: string; rowId?: string; label: string } | null {
  if (!pending || pending.kind === "stop") return null
  const stage = String(pending.stage ?? pending.needed_for_stage ?? "")
  const section = sections.find((s) => s.stage === stage)
  if (!section || !stages[stage]) return null
  const rowId = String(pending.row_id ?? pending.item ?? "")
  const row = rowId ? stages[stage]?.rows.find((r) => r.id === rowId) : undefined
  return row ? { stage, rowId, label: row.label || row.id } : { stage, label: section.title }
}
